import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { toast } from 'react-hot-toast';
import { bankApi } from '../services/api';
// import { useUser } from '../contexts/UserContext'; // Not needed for this component
import LoadingSpinner from './UI/LoadingSpinner';

interface BankAccount {
  id: string;
  account_id: string;
  account_number_last4: string;
  routing_number: string;
  bank_name: string;
  account_type: 'checking' | 'savings';
  status: 'active' | 'inactive' | 'pending';
  created_at: string;
}

interface BankTransferProps {
  type: 'deposit' | 'withdraw';
  onSuccess?: () => void;
}

const BankTransfer: React.FC<BankTransferProps> = ({ type, onSuccess }) => {
  // const { user } = useUser(); // Not needed for this component
  const [isLoading, setIsLoading] = useState(false);
  const [accounts, setAccounts] = useState<BankAccount[]>([]);
  const [selectedAccount, setSelectedAccount] = useState<string>('');
  const [amount, setAmount] = useState<string>('');
  const [description, setDescription] = useState<string>('');
  const [isProcessing, setIsProcessing] = useState(false);
  const [balance, setBalance] = useState<{
    available_balance: number;
    pending_balance: number;
    total_balance: number;
    currency: string;
  } | null>(null);

  useEffect(() => {
    loadBankAccounts();
    loadBalance();
  }, []);

  const loadBankAccounts = async () => {
    try {
      setIsLoading(true);
      const response = await bankApi.getAccounts();
      if (response.data && response.data.success && response.data.data) {
        const activeAccounts = response.data.data.accounts.filter((acc: BankAccount) => acc.status === 'active');
        setAccounts(activeAccounts);
        if (activeAccounts.length === 1) {
          setSelectedAccount(activeAccounts[0].id);
        }
      }
    } catch (error) {
      console.error('Failed to load bank accounts:', error);
      toast.error('Failed to load bank accounts');
    } finally {
      setIsLoading(false);
    }
  };

  const loadBalance = async () => {
    try {
      const response = await bankApi.getBalance();
      if (response.data && response.data.success && response.data.data) {
        setBalance({
          available_balance: response.data.data.balanceCents / 100,
          pending_balance: 0,
          total_balance: response.data.data.balanceCents / 100,
          currency: 'USD'
        });
      }
    } catch (error) {
      console.error('Failed to load balance:', error);
      // Don't show error toast for balance loading
    }
  };

  const handleTransfer = async () => {
    if (!selectedAccount || !amount) {
      toast.error('Please select an account and enter an amount');
      return;
    }

    const numAmount = parseFloat(amount);
    if (isNaN(numAmount) || numAmount <= 0) {
      toast.error('Please enter a valid amount');
      return;
    }

    if (type === 'withdraw' && balance && numAmount > balance.available_balance) {
      toast.error('Insufficient funds for withdrawal');
      return;
    }

    if (numAmount < 1) {
      toast.error('Minimum transfer amount is $1.00');
      return;
    }

    if (numAmount > 10000) {
      toast.error('Maximum transfer amount is $10,000.00');
      return;
    }

    try {
      setIsProcessing(true);

      const response = await bankApi.transfer({
        bank_account_id: selectedAccount,
        amount_cents: Math.round(numAmount * 100),
        direction: type,
      });

      if (response.data.success && response.data.data) {
        toast.success(
          `${type === 'deposit' ? 'Deposit' : 'Withdrawal'} initiated successfully!`
        );

        // Reset form
        setAmount('');
        setDescription('');
        setSelectedAccount(accounts.length === 1 ? accounts[0].id : '');

        // Reload balance
        loadBalance();

        onSuccess?.();
      } else {
        toast.error(response.data.message || `Failed to initiate ${type}`);
      }
    } catch (error) {
      console.error(`Failed to ${type}:`, error);
      toast.error(`Failed to initiate ${type}`);
    } finally {
      setIsProcessing(false);
    }
  };

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    }).format(amount);
  };

  const formatAccountType = (type: string) => {
    return type.charAt(0).toUpperCase() + type.slice(1);
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center p-8">
        <LoadingSpinner size="lg" />
      </div>
    );
  }

  if (accounts.length === 0) {
    return (
      <div className="text-center p-8">
        <div className="p-4 bg-neutral-600 rounded-full w-16 h-16 mx-auto mb-4 flex items-center justify-center">
          <svg className="w-8 h-8 text-neutral-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 10h18M7 15h1m4 0h1m-7 4h12a3 3 0 003-3V8a3 3 0 00-3-3H6a3 3 0 00-3 3v8a3 3 0 003 3z" />
          </svg>
        </div>
        <h4 className="text-lg font-semibold text-neutral-100 mb-2">
          No Bank Accounts Linked
        </h4>
        <p className="text-neutral-400 mb-4">
          Please link a bank account first to {type === 'deposit' ? 'add funds' : 'withdraw funds'}.
        </p>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="text-center">
        <h3 className="text-xl font-semibold text-neutral-100 mb-2">
          {type === 'deposit' ? 'Add Funds' : 'Withdraw Funds'}
        </h3>
        <p className="text-neutral-400">
          {type === 'deposit'
            ? 'Deposit money from your bank account to fund investments'
            : 'Withdraw funds to your linked bank account'
          }
        </p>
      </div>

      {/* Balance Display */}
      {balance && (
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="card p-4"
        >
          <div className="grid grid-cols-3 gap-4 text-center">
            <div>
              <p className="text-sm text-neutral-400">Available</p>
              <p className="text-lg font-semibold text-green-400">
                {formatCurrency(balance.available_balance)}
              </p>
            </div>
            <div>
              <p className="text-sm text-neutral-400">Pending</p>
              <p className="text-lg font-semibold text-yellow-400">
                {formatCurrency(balance.pending_balance)}
              </p>
            </div>
            <div>
              <p className="text-sm text-neutral-400">Total</p>
              <p className="text-lg font-semibold text-neutral-100">
                {formatCurrency(balance.total_balance)}
              </p>
            </div>
          </div>
        </motion.div>
      )}

      {/* Transfer Form */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.1 }}
        className="card p-6"
      >
        <div className="space-y-6">
          {/* Account Selection */}
          <div>
            <label className="block text-sm font-medium text-neutral-300 mb-2">
              Select Bank Account
            </label>
            <div className="space-y-2">
              {accounts.map((account) => (
                <div
                  key={account.id}
                  onClick={() => setSelectedAccount(account.id)}
                  className={`p-4 border rounded-lg cursor-pointer transition-colors ${
                    selectedAccount === account.id
                      ? 'border-honey-500 bg-honey-500 bg-opacity-10'
                      : 'border-neutral-600 bg-neutral-700 hover:bg-neutral-650'
                  }`}
                >
                  <div className="flex items-center justify-between">
                    <div className="flex items-center space-x-3">
                      <div className="p-2 bg-neutral-600 rounded-full">
                        <svg className="w-5 h-5 text-neutral-300" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" />
                        </svg>
                      </div>
                      <div>
                        <p className="font-medium text-neutral-100">
                          {account.bank_name} ••••{account.account_number_last4}
                        </p>
                        <p className="text-sm text-neutral-400">
                          {formatAccountType(account.account_type)}
                        </p>
                      </div>
                    </div>
                    {selectedAccount === account.id && (
                      <div className="text-honey-500">
                        <svg className="w-6 h-6" fill="currentColor" viewBox="0 0 20 20">
                          <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                        </svg>
                      </div>
                    )}
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Amount Input */}
          <div>
            <label className="block text-sm font-medium text-neutral-300 mb-2">
              Amount (USD)
            </label>
            <div className="relative">
              <span className="absolute left-3 top-1/2 transform -translate-y-1/2 text-neutral-400">$</span>
              <input
                type="number"
                value={amount}
                onChange={(e) => setAmount(e.target.value)}
                placeholder="0.00"
                min="1"
                max="10000"
                step="0.01"
                className="w-full pl-8 pr-4 py-3 bg-neutral-700 border border-neutral-600 rounded-lg text-neutral-100 placeholder-neutral-400 focus:outline-none focus:ring-2 focus:ring-honey-500 focus:border-transparent"
              />
            </div>
            <p className="text-xs text-neutral-500 mt-1">
              Minimum: $1.00 • Maximum: $10,000.00
            </p>
          </div>

          {/* Description */}
          <div>
            <label className="block text-sm font-medium text-neutral-300 mb-2">
              Description (Optional)
            </label>
            <input
              type="text"
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              placeholder={`${type === 'deposit' ? 'Deposit' : 'Withdrawal'} for investments`}
              maxLength={100}
              className="w-full px-4 py-3 bg-neutral-700 border border-neutral-600 rounded-lg text-neutral-100 placeholder-neutral-400 focus:outline-none focus:ring-2 focus:ring-honey-500 focus:border-transparent"
            />
          </div>

          {/* Transfer Button */}
          <button
            onClick={handleTransfer}
            disabled={isProcessing || !selectedAccount || !amount}
            className="w-full btn-primary flex items-center justify-center space-x-2 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {isProcessing ? (
              <>
                <LoadingSpinner size="sm" />
                <span>Processing...</span>
              </>
            ) : (
              <>
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d={
                    type === 'deposit'
                      ? "M12 4v16m8-8H4"
                      : "M3 10h18M7 15h1m4 0h1m-7 4h12a3 3 0 003-3V8a3 3 0 00-3-3H6a3 3 0 00-3 3v8a3 3 0 003 3z"
                  } />
                </svg>
                <span>
                  {type === 'deposit' ? 'Deposit Funds' : 'Withdraw Funds'}
                </span>
              </>
            )}
          </button>
        </div>
      </motion.div>

      {/* Important Notes */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.2 }}
        className="bg-neutral-800 border border-neutral-700 rounded-lg p-4"
      >
        <div className="flex items-start space-x-3">
          <div className="p-2 bg-blue-500 bg-opacity-20 rounded-full flex-shrink-0">
            <svg className="w-5 h-5 text-blue-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
          </div>
          <div>
            <h5 className="text-sm font-semibold text-neutral-100 mb-1">
              Important Information
            </h5>
            <ul className="text-xs text-neutral-400 space-y-1">
              <li>• {type === 'deposit' ? 'Deposits' : 'Withdrawals'} typically take 1-3 business days to process</li>
              <li>• All transfers are subject to review for compliance and fraud prevention</li>
              <li>• {type === 'withdraw' ? 'Withdrawal' : 'Deposit'} limits may apply based on your account verification status</li>
              <li>• Contact support if you need assistance with your transfer</li>
            </ul>
          </div>
        </div>
      </motion.div>
    </div>
  );
};

export default BankTransfer;